import { useCallback, useEffect, useMemo } from 'react'
import { AnyExtension, Editor, useEditor } from '@tiptap/react'
import { isDeepEqualData } from '@deepguide-ai/shared'
import { createAIExtensions } from '../tiptap-extensions/ai'
import { AiSuggestionOptions } from '../tiptap-extensions/ai/ai-suggestion'
import { CommonExtensions } from '../tiptap-extensions/common'
import { CustomExtensions } from '../tiptap-extensions/custom'
import { CommentsExtension } from '../tiptap-extensions/custom/comments'
import { getCommentStorage } from '../tiptap-extensions/custom/comments/storage'
import type { Comment } from '../tiptap-extensions/custom/comments/types'
import { CustomComponentsExtensions } from '../tiptap-extensions/ui-components'

/**
 * useAiEditor
 *
 * A custom hook to initialize a Tiptap editor instance with AI and common extensions.
 *
 * @param {object} options - Options for the editor.
 * @param {any} options.content - Initial content for the editor. HTML, Markdown, or JSON
 * @param {(editor: Editor) => void} [options.onCreated] - Optional callback when editor is created.
 * @returns {{
 *   editor: Editor | null,
 *   isReady: boolean,
 * }}
 */
export function useAiEditor({
  content,
  onCreate,
  onUpdate,
  suggestionOptions,
  // changesOptions,
  extensions = [],
  markdownMode = false, // NEW: if true, treat content as Markdown/MDX
  readOnly = false,
  reviewMode = false,
  commentsConfig,
  stripCommentMarks = false,
}: {
  content: any
  onCreate?: ({ editor }: { editor: Editor }) => void
  onUpdate?: ({ editor }: { editor: Editor }) => void
  suggestionOptions?: AiSuggestionOptions & {
    popoverRef?: React.RefObject<HTMLElement | null>
  }
  // changesOptions?: {
  //   popoverRef?: React.RefObject<HTMLElement | null>
  // }
  extensions?: AnyExtension[]
  markdownMode?: boolean // NEW
  readOnly: boolean
  /** When true, content is readonly but commenting is allowed (for auditor review workflows) */
  reviewMode?: boolean
  commentsConfig?: {
    enabled: boolean
    docId: string
    workspaceId: string
    branchId?: string
    changeRequestId?: string
    onCommentsUpdated?: (comments: Comment[]) => void
    onCommentActivated?: (commentId: string | null) => void
    onCommentsDataUpdated?: (comments: Map<string, Comment> | null) => void
    onError?: (message: string, error: any) => void
  }
  stripCommentMarks?: boolean
}) {
  // 工具: 判断节点是否为 inline 类型
  const isInlineType = (type?: string) => {
    if (!type) return false
    // 依据当前已启用的扩展，列出内联节点类型
    return (
      type === 'text' ||
      type === 'hardBreak' ||
      type === 'inlineImage' ||
      type === 'inlineButton' ||
      type === 'emoji' ||
      type === 'mention' ||
      type === 'Glossary' ||
      type === 'Citation' ||
      type === 'sourceCitation'
    )
  }

  const extractPlainTextFromContent = (nodes?: any[]): string => {
    if (!Array.isArray(nodes)) return ''
    let result = ''
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue
      if (typeof node.text === 'string') {
        result += node.text
      } else if (Array.isArray(node.content)) {
        result += extractPlainTextFromContent(node.content)
      }
    }
    return result
  }

  const toBoolean = (value: any): boolean | undefined => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      if (value === 'true') return true
      if (value === 'false') return false
    }
    return undefined
  }

  const transformLegacyDetailsNode = (detailsNode: any) => {
    const rawChildren = Array.isArray(detailsNode?.content)
      ? detailsNode.content
      : []
    let title = ''
    const bodyNodes: any[] = []

    for (const childNode of rawChildren) {
      if (!childNode || typeof childNode !== 'object') continue

      if (
        childNode.type === 'paragraph' &&
        Array.isArray(childNode.content)
      ) {
        const remainingInline: any[] = []

        for (const inlineNode of childNode.content) {
          if (!inlineNode || typeof inlineNode !== 'object') continue

          if (inlineNode.type === 'summary') {
            if (!title) {
              const summaryText = extractPlainTextFromContent(
                inlineNode.content
              )
              if (summaryText.trim().length > 0) {
                title = summaryText.trim()
              }
            }
            continue
          }

          remainingInline.push(inlineNode)
        }

        if (remainingInline.length > 0) {
          bodyNodes.push({
            ...childNode,
            content: remainingInline,
          })
        }
        continue
      }

      if (childNode.type === 'summary') {
        if (!title) {
          const summaryText = extractPlainTextFromContent(childNode.content)
          if (summaryText.trim().length > 0) {
            title = summaryText.trim()
          }
        }
        continue
      }

      bodyNodes.push(childNode)
    }

    if (!title) {
      title =
        (typeof detailsNode?.attrs?.title === 'string'
          ? detailsNode.attrs.title
          : '') || 'Toggle'
    }

    const attrs: Record<string, any> = {}
    if (detailsNode?.attrs?.id) {
      attrs.id = detailsNode.attrs.id
    }
    const defaultOpenAttr =
      detailsNode?.attrs?.defaultOpen ?? detailsNode?.attrs?.open
    const coercedDefaultOpen = toBoolean(defaultOpenAttr)
    if (typeof coercedDefaultOpen === 'boolean') {
      attrs.defaultOpen = coercedDefaultOpen
    }

    return { title, bodyNodes, attrs }
  }

  // 规范化内容: 确保 paragraph 的子节点全部为 inline
  const normalizeNodes = (
    nodes: any[] | undefined,
    parentType?: string
  ): any[] => {
    if (!Array.isArray(nodes)) return []
    const out: any[] = []

    for (const child of nodes) {
      if (!child || typeof child !== 'object') continue

      if (child.type === 'summary') {
        const flattened = child.content
          ? normalizeNodes(child.content, parentType)
          : []
        if (flattened.length > 0) {
          out.push(...flattened)
        }
        continue
      }

      if (child.type === 'details') {
        const { title, bodyNodes, attrs } = transformLegacyDetailsNode(child)
        const normalizedBody = normalizeNodes(bodyNodes, 'toggle')
        const contentForToggle =
          normalizedBody.length > 0
            ? normalizedBody
            : [{ type: 'paragraph', content: [] }]

        const toggleAttrs: Record<string, any> = {
          title: title || 'Toggle',
          defaultOpen:
            typeof attrs.defaultOpen === 'boolean' ? attrs.defaultOpen : false,
        }
        if (attrs.id) {
          toggleAttrs.id = attrs.id
        }

        out.push({
          type: 'toggle',
          attrs: toggleAttrs,
          content: contentForToggle,
        })
        continue
      }

      // 深度优先处理子内容
      const normalizedChildContent = child.content
        ? normalizeNodes(child.content, child.type)
        : undefined

      if (child.type === 'Step') {
        const ensureBlockContent = (nodes?: any[]) => {
          if (!Array.isArray(nodes) || nodes.length === 0) {
            return [{ type: 'paragraph', content: [] }]
          }

          const blocks: any[] = []
          let inlineBuffer: any[] = []

          const flushInline = () => {
            if (inlineBuffer.length > 0) {
              blocks.push({ type: 'paragraph', content: [...inlineBuffer] })
              inlineBuffer = []
            }
          }

          for (const node of nodes) {
            if (!node || typeof node !== 'object') continue
            if (isInlineType(node.type)) {
              inlineBuffer.push(node)
              continue
            }
            flushInline()
            blocks.push(node)
          }

          flushInline()

          if (blocks.length === 0) {
            blocks.push({ type: 'paragraph', content: [] })
          }

          return blocks
        }

        const nodeCopy: any = { ...child }
        nodeCopy.attrs = {
          showHeader: true,
          showDescription: true,
          showNumber: true,
          tagType: 'none',
          ...nodeCopy.attrs,
        }
        nodeCopy.content = ensureBlockContent(normalizedChildContent)
        out.push(nodeCopy)
        continue
      }

      if (child.type === 'paragraph') {
        const attrs = child.attrs || {}
        const inlineBuffer: any[] = []

        const flushParagraph = () => {
          if (inlineBuffer.length > 0) {
            out.push({ type: 'paragraph', attrs, content: [...inlineBuffer] })
            inlineBuffer.length = 0
          } else if (parentType === 'listItem' || parentType === 'taskItem') {
            // 对于列表项，即使没有内容也要保留段落节点
            out.push({ type: 'paragraph', attrs, content: [] })
          } else {
            console.log(
              '🔍 [normalizeNodes] flushParagraph - 跳过空段落，父类型:',
              parentType
            )
          }
        }

        const children = normalizedChildContent || []
        // console.log('🔍 [normalizeNodes] 段落子节点数量:', children.length)

        // 定义功能性扩展和合法块级节点
        const functionalExtensions = [
          'gapcursor',
          'history',
          'dropcursor',
          'selection',
        ]
        const validBlockNodes = [
          'paragraph',
          'heading',
          'blockquote',
          'codeBlock',
          'bulletList',
          'orderedList',
          'listItem',
          'table',
          'tableRow',
          'tableHeader',
          'tableCell',
          'horizontalRule',
          'Card',
          'CardGroup',
          'Step',
          'Steps',
          'Tab',
          'Tabs',
          'Admonition',
          'Accordion',
          'AccordionGroup',
          'Toggle',
          'toggle',
          'Screenshot',
          'FolderContents',
          'mermaid',
          'columns',
          'column',
          'taskList',
          'taskItem',
          'imageUpload',
        ]

        for (const n of children) {
          if (!n?.type) {
            continue // 跳过无效节点
          }

          if (isInlineType(n.type)) {
            inlineBuffer.push(n)
          } else if (functionalExtensions.includes(n.type)) {
            // 过滤掉功能性扩展，不加入输出
            console.log(
              `[normalizeNodes] Filtering out functional extension: ${n.type}`
            )
            continue
          } else if (validBlockNodes.includes(n.type)) {
            // 合法的块级节点，保留
            flushParagraph()
            out.push(n)
          } else {
            // 未知节点类型，记录警告但保留（避免内容丢失）
            console.warn(
              `[normalizeNodes] Unknown node type in paragraph: ${n.type}`
            )
            flushParagraph()
            out.push(n)
          }
        }
        // 收尾: 剩余 inline 输出为一个段落
        flushParagraph()
      } else {
        // 非 paragraph: 按原结构回填（内部已递归规范化）
        const nodeCopy: any = { ...child }
        if (normalizedChildContent) nodeCopy.content = normalizedChildContent
        out.push(nodeCopy)
      }
    }

    return out
  }

  const normalizeDoc = useCallback((doc: any) => {
    if (!doc || typeof doc !== 'object') return doc
    if (doc.type !== 'doc') return doc
    return {
      ...doc,
      content: normalizeNodes(doc.content, 'doc'),
    }
  }, []) // normalizeNodes is stable (defined above, doesn't change)

  // Remove comment marks/attrs when the consumer requests stripping (e.g., unlock mode without comments extension)
  const stripCommentMarksFromDoc = useCallback((node: any): any => {
    if (!node || typeof node !== 'object') return node

    const cleaned: any = { ...node }

    if (Array.isArray(node.content)) {
      cleaned.content = node.content
        .map((child: any) => stripCommentMarksFromDoc(child))
        .filter(Boolean)
    }

    if (Array.isArray(node.marks)) {
      cleaned.marks = node.marks
        .map((mark: any) => {
          if (!mark || typeof mark !== 'object') return mark
          if (mark.type === 'comment') return null
          const markCopy: any = { ...mark }
          if (markCopy.attrs) {
            const {
              commentId,
              ['data-comment-id']: _commentId,
              ['data-comment-source']: _commentSource,
              ['data-comment-severity']: _commentSeverity,
              ...restMarkAttrs
            } = markCopy.attrs
            markCopy.attrs = restMarkAttrs
          }
          return markCopy
        })
        .filter(Boolean)
    }

    if (cleaned.attrs) {
      const {
        commentId,
        ['data-comment-id']: _commentId,
        ['data-comment-source']: _commentSource,
        ['data-comment-severity']: _commentSeverity,
        ...rest
      } = cleaned.attrs
      cleaned.attrs = rest
    }

    return cleaned
  }, [])

  // CRITICAL FIX: Memoize extensions to prevent editor recreation
  const memoizedExtensions = useMemo(() => {
    // console.log('🔄 [useAiEditor] Memoizing extensions', {
    //   extensionsLength: extensions.length,
    //   hasSuggestionOptions: !!suggestionOptions,
    //   hasChangesOptions: !!changesOptions
    // })
    const baseExtensions = [
      ...CommonExtensions,
      ...CustomExtensions,
      ...CustomComponentsExtensions,
      // StreamContentExtension,
      ...createAIExtensions({
        suggestionOptions,
        // changesOptions,
      }),
    ]

    // Add comments extension if enabled
    if (
      commentsConfig?.enabled &&
      commentsConfig.docId &&
      commentsConfig.workspaceId
    ) {
      const storage = getCommentStorage()
      baseExtensions.push(
        CommentsExtension.configure({
          storage,
          docId: commentsConfig.docId,
          workspaceId: commentsConfig.workspaceId,
          branchId: commentsConfig.branchId,
          changeRequestId: commentsConfig.changeRequestId,
          onCommentsUpdated: commentsConfig.onCommentsUpdated || (() => {}),
          onCommentActivated: commentsConfig.onCommentActivated || (() => {}),
          onCommentsDataUpdated:
            commentsConfig.onCommentsDataUpdated || (() => {}),
          onError: commentsConfig.onError,
        })
      )
    }

    return [...baseExtensions, ...extensions]
  }, [suggestionOptions, extensions, commentsConfig])

  // CRITICAL FIX: Create editor without content dependency to prevent recreation
  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: true,
    enableContentCheck: false,
    emitContentError: true,
    editable: !readOnly,
    editorProps: {
      attributes: {
        spellcheck: 'false',
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        'aria-label': 'AI-powered editor, start typing to enter text.',
      },
    },
    extensions: memoizedExtensions,
    onUpdate: onUpdate,
    onCreate: onCreate,
  })

  useEffect(() => {
    if (editor && content) {
      const current = editor.getJSON()
      const normalized =
        typeof content === 'object' ? normalizeDoc(content) : content
      const sanitized =
        stripCommentMarks && typeof normalized === 'object'
          ? stripCommentMarksFromDoc(normalized)
          : normalized

      if (isDeepEqualData(current, sanitized)) {
        return
      }
      queueMicrotask(() => {
        if (markdownMode && typeof sanitized === 'string') {
          // editor.commands.setContent(parseMarkdownToTiptapDoc(normalized))
        } else {
          try {
            // Check if user is interacting with a floating UI element or chat textarea
            const activeElement = document.activeElement
            const isInFloatingUI = activeElement?.closest('[data-slot="command-input"]') ||
                                   activeElement?.closest('[role="dialog"]') ||
                                   activeElement?.closest('[data-radix-popper-content-wrapper]')

            // Check if user is typing in chat panel textarea
            const isInChatTextarea = activeElement?.tagName === 'TEXTAREA' &&
                                   activeElement?.getAttribute('name') === 'message'

            // Set content first, then safely position cursor at start
            editor.commands.setContent(sanitized as any)

            // Only set selection if document has valid content positions
            const docSize = editor.state.doc.content.size
            if (docSize > 0) {
              // Position 1 is inside first block (position 0 is doc node itself)
              const safePos = Math.min(1, docSize)
              editor.commands.setTextSelection(safePos)
            }

            // Only blur if user is not actively interacting with UI elements
            if (!isInFloatingUI && !isInChatTextarea) {
              editor.commands.blur()
            }
          } catch (error) {
            console.error('Error setting content', error)
          }
        }
      })
    }
  }, [editor, content, markdownMode, normalizeDoc, stripCommentMarks, stripCommentMarksFromDoc])

  // Helper: export current doc to Markdown/MDX
  // const exportMarkdown = () => {
  //   if (!editor) return ''
  //   return serializeTiptapDocToMarkdown(editor.getJSON())
  // }

  // Update editable state when readOnly prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly)
      ;(editor as any).emit('editableChange', { editor })
    }
  }, [editor, readOnly])

  // Store reviewMode in editor storage for node views to access
  useEffect(() => {
    if (editor) {
      // Store reviewMode on editor instance for node views to check
      ;(editor as any).reviewMode = reviewMode
    }
  }, [editor, reviewMode])

  return { editor }
}
